import { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Save, Send, Search, Check, X } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import type { PaginatedResponse } from '@/types';

// Durchsuchbare Gefangenenauswahl Komponente
interface Prisoner {
  id: string;
  prisonerNumber: string;
  firstName: string;
  lastName: string;
}

interface CreateWorkflowResponse {
  id: string;
  workflowNumber: string;
}

function PrisonerSearchSelect({
  value,
  onChange,
  prisoners,
  isLoading = false,
}: {
  value: string;
  onChange: (value: string) => void;
  prisoners: Prisoner[];
  isLoading?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Schließen bei Klick außerhalb
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Nur schließen, wenn wirklich außerhalb geklickt wurde
      const target = event.target as Node;
      if (
        containerRef.current && 
        !containerRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    
    // Verzögert hinzufügen, um initiales Klicken zu ermöglichen
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Gefilterte Liste mit Relevanz-Sortierung
  const filteredPrisoners = useMemo(() => {
    if (!prisoners || prisoners.length === 0) return [];
    
    if (!searchTerm) {
      // Ohne Suchbegriff: alphabetisch nach Nachname sortiert
      return [...prisoners].sort((a, b) => a.lastName.localeCompare(b.lastName));
    }
    
    const term = searchTerm.toLowerCase().trim();
    if (!term) return [...prisoners].sort((a, b) => a.lastName.localeCompare(b.lastName));
    
    // Filtere und berechne Relevanz
    const scored = prisoners
      .map((p) => {
        const fullName = `${p.lastName}, ${p.firstName}`.toLowerCase();
        const reverseName = `${p.firstName} ${p.lastName}`.toLowerCase();
        const number = p.prisonerNumber.toLowerCase();
        
        let score = 0;
        
        // Exakter Match am Anfang = höchste Relevanz
        if (p.lastName.toLowerCase().startsWith(term)) score += 100;
        else if (p.firstName.toLowerCase().startsWith(term)) score += 90;
        else if (number.startsWith(term)) score += 85;
        
        // Enthält den Begriff
        if (p.lastName.toLowerCase().includes(term)) score += 50;
        if (p.firstName.toLowerCase().includes(term)) score += 40;
        if (number.includes(term)) score += 30;
        if (fullName.includes(term)) score += 20;
        if (reverseName.includes(term)) score += 20;
        
        return { prisoner: p, score };
      })
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score);
    
    return scored.map((item) => item.prisoner);
  }, [prisoners, searchTerm]);

  // Ausgewählter Gefangener
  const selectedPrisoner = useMemo(() => 
    prisoners.find((p) => p.id === value), 
    [prisoners, value]
  );

  const handleSelect = (prisonerId: string) => {
    onChange(prisonerId);
    setIsOpen(false);
    setSearchTerm('');
    inputRef.current?.blur();
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (!isOpen) setIsOpen(true);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  // Shortlist: Zeige immer max. 10 Ergebnisse in der Vorschau
  const displayCount = searchTerm ? 20 : 10;
  const displayPrisoners = filteredPrisoners.slice(0, displayCount);
  const hasMore = filteredPrisoners.length > displayCount;

  // Highlight-Funktion für Suchbegriff
  const highlightMatch = (text: string): React.ReactNode => {
    if (!searchTerm || !searchTerm.trim()) return text;
    try {
      const regex = new RegExp(`(${searchTerm.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      const parts = text.split(regex);
      return parts.map((part, i) =>
        regex.test(part) ? (
          <mark key={i} className="bg-yellow-200 rounded px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      );
    } catch {
      return text;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Suchfeld - immer sichtbar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          placeholder={selectedPrisoner 
            ? `${selectedPrisoner.lastName}, ${selectedPrisoner.firstName} (${selectedPrisoner.prisonerNumber})`
            : "Gefangenen suchen (Name oder Nummer)..."
          }
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          className={`pl-9 pr-8 ${selectedPrisoner && !searchTerm ? 'placeholder:text-foreground' : ''}`}
          autoComplete="off"
        />
        {(selectedPrisoner || searchTerm) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={handleClear}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Ausgewählter Gefangener Anzeige */}
      {selectedPrisoner && !isOpen && (
        <div className="mt-1 px-2 py-1.5 bg-primary/10 rounded text-sm flex items-center gap-2">
          <Check className="h-4 w-4 text-primary flex-shrink-0" />
          <span className="font-medium">{selectedPrisoner.lastName}, {selectedPrisoner.firstName}</span>
          <span className="text-muted-foreground">({selectedPrisoner.prisonerNumber})</span>
        </div>
      )}

      {/* Dropdown-Liste / Shortlist */}
      {isOpen && (
        <div 
          ref={dropdownRef}
          className="absolute z-[100] mt-1 w-full rounded-md border bg-popover shadow-xl max-h-80 overflow-auto"
        >
          {/* Header mit Anzahl */}
          <div className="px-3 py-2 text-xs text-muted-foreground border-b bg-muted/50 sticky top-0 z-10">
            {isLoading ? (
              <>Lade Gefangene...</>
            ) : searchTerm ? (
              <>{filteredPrisoners.length} Treffer für "<strong>{searchTerm}</strong>"</>
            ) : (
              <>Gefangene auswählen ({prisoners.length} gesamt)</>
            )}
          </div>
          
          {isLoading ? (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2" />
              Lade Gefangenenliste...
            </div>
          ) : displayPrisoners.length === 0 ? (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">
              {searchTerm ? (
                <>Keine Gefangenen gefunden für "<strong>{searchTerm}</strong>"</>
              ) : (
                <>Keine Gefangenen verfügbar</>
              )}
            </div>
          ) : (
            <div className="py-1">
              {displayPrisoners.map((prisoner) => (
                <button
                  key={prisoner.id}
                  type="button"
                  className={`w-full px-3 py-2.5 text-left text-sm hover:bg-accent flex items-center justify-between transition-colors cursor-pointer ${
                    prisoner.id === value ? 'bg-primary/10 border-l-2 border-primary' : ''
                  }`}
                  onClick={() => handleSelect(prisoner.id)}
                  onMouseDown={(e) => e.preventDefault()} // Verhindert Blur vor Click
                >
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">
                      {highlightMatch(prisoner.lastName)}, {highlightMatch(prisoner.firstName)}
                    </span>
                    <span className="text-muted-foreground ml-2 text-xs">
                      {highlightMatch(prisoner.prisonerNumber)}
                    </span>
                  </div>
                  {prisoner.id === value && <Check className="h-4 w-4 text-primary flex-shrink-0 ml-2" />}
                </button>
              ))}
              
              {hasMore && (
                <div className="px-3 py-2 text-xs text-muted-foreground border-t bg-muted/30 text-center">
                  +{filteredPrisoners.length - displayCount} weitere Treffer
                  {!searchTerm && " – tippen Sie, um zu filtern"}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const WORKFLOW_TYPES = [
  { value: 'BESONDERES_VORKOMMNIS', label: 'Besonderes Vorkommnis' },
  { value: 'AUFFAELLIGKEIT', label: 'Auffälligkeit / Beobachtung' },
  { value: 'DISZIPLINAR_ANZEIGE', label: 'Disziplinarischen Sachverhalt anzeigen' },
  { value: 'BESUCH_VORKOMMNIS', label: 'Besuchsbezogenes Vorkommnis' },
  { value: 'VERLEGUNG_ANREGUNG', label: 'Verlegung / Unterbringungsänderung anregen' },
  { value: 'ARBEIT_VORKOMMNIS', label: 'Arbeits-/Beschäftigungsvorkommnis' },
  { value: 'EILMELDUNG', label: 'Eil-/Sofortmeldung' },
];

const INCIDENT_TYPES: Record<string, { value: string; label: string }[]> = {
  BESONDERES_VORKOMMNIS: [
    { value: 'GEWALT', label: 'Gewalt' },
    { value: 'DROHUNG', label: 'Drohung' },
    { value: 'SACHBESCHAEDIGUNG', label: 'Sachbeschädigung' },
    { value: 'WIDERSTAND', label: 'Widerstand' },
    { value: 'FLUCHTANZEICHEN', label: 'Fluchtanzeichen' },
    { value: 'SELBSTVERLETZUNG', label: 'Selbstverletzung' },
    { value: 'FUND_VERBOTENER_GEGENSTAND', label: 'Fund verbotener Gegenstand' },
    { value: 'SONSTIGES_VORKOMMNIS', label: 'Sonstiges' },
  ],
  AUFFAELLIGKEIT: [
    { value: 'VERHALTENSAENDERUNG', label: 'Verhaltensänderung' },
    { value: 'KONFLIKT', label: 'Konflikt' },
    { value: 'RUECKZUG', label: 'Rückzug' },
    { value: 'AUFFAELLIGE_STIMMUNG', label: 'Auffällige Stimmung' },
    { value: 'GESUNDHEITLICH_AUFFAELLIG', label: 'Gesundheitlich auffällig' },
    { value: 'SONSTIGE_AUFFAELLIGKEIT', label: 'Sonstige Auffälligkeit' },
  ],
  DISZIPLINAR_ANZEIGE: [
    { value: 'ORDNUNGSWIDRIGKEIT', label: 'Ordnungswidrigkeit' },
    { value: 'ARBEITSVERWEIGERUNG', label: 'Arbeitsverweigerung' },
    { value: 'VERSTOSS_HAUSORDNUNG', label: 'Verstoß gegen Hausordnung' },
    { value: 'UNERLAUBTER_BESITZ', label: 'Unerlaubter Besitz' },
    { value: 'SONSTIGER_VERSTOSS', label: 'Sonstiger Verstoß' },
  ],
  BESUCH_VORKOMMNIS: [
    { value: 'BESUCH_ABGEBROCHEN', label: 'Besuch abgebrochen' },
    { value: 'BESUCH_VERWEIGERT', label: 'Besuch verweigert' },
    { value: 'KONTAKTVERSUCH_UNERLAUBT', label: 'Unerlaubter Kontaktversuch' },
    { value: 'GEGENSTAND_UEBERGABE', label: 'Gegenstandsübergabe' },
    { value: 'SONSTIGES_BESUCH', label: 'Sonstiges' },
  ],
  VERLEGUNG_ANREGUNG: [
    { value: 'SONSTIGES_VORKOMMNIS', label: 'Verlegungsanregung' },
  ],
  ARBEIT_VORKOMMNIS: [
    { value: 'ARBEITSUNFALL', label: 'Arbeitsunfall' },
    { value: 'ARBEITSVERWEIGERUNG_ARBEIT', label: 'Arbeitsverweigerung' },
    { value: 'KONFLIKT_ARBEITSPLATZ', label: 'Konflikt am Arbeitsplatz' },
    { value: 'QUALITAETSMANGEL', label: 'Qualitätsmangel' },
    { value: 'SONSTIGES_ARBEIT', label: 'Sonstiges' },
  ],
  EILMELDUNG: [
    { value: 'GEWALT', label: 'Gewalt' },
    { value: 'SELBSTVERLETZUNG', label: 'Selbstverletzung' },
    { value: 'FLUCHTANZEICHEN', label: 'Fluchtanzeichen' },
    { value: 'SONSTIGES_VORKOMMNIS', label: 'Sonstiges' },
  ],
};

const LOCATIONS = [
  { value: 'HAFTRAUM', label: 'Haftraum' },
  { value: 'GEMEINSCHAFTSRAUM', label: 'Gemeinschaftsraum' },
  { value: 'ARBEITSBEREICH', label: 'Arbeitsbereich' },
  { value: 'FREISTUNDE', label: 'Freistunde' },
  { value: 'BESUCHSBEREICH', label: 'Besuchsbereich' },
  { value: 'VERWALTUNG', label: 'Verwaltung' },
  { value: 'EINGANGSBEREICH', label: 'Eingangsbereich' },
  { value: 'SPORT', label: 'Sport' },
  { value: 'SCHULE', label: 'Schule' },
  { value: 'WERKSTATT', label: 'Werkstatt' },
  { value: 'KUECHE', label: 'Küche' },
  { value: 'SONSTIGER_ORT', label: 'Sonstiger Ort' },
];

const PRIORITIES = [
  { value: 'NIEDRIG', label: 'Niedrig' },
  { value: 'NORMAL', label: 'Normal' },
  { value: 'HOCH', label: 'Hoch' },
  { value: 'EILSACHE', label: 'Eilsache' },
];

interface FormData {
  prisonerId: string;
  workflowType: string;
  incidentType: string;
  incidentDate: string;
  incidentTime: string;
  location: string;
  locationDetail: string;
  description: string;
  immediateMeasures: string;
  witnesses: string;
  priority: string;
  dueDate: string;
  transferType: string;
  targetFacility: string;
  transferReason: string;
}

export function NewVgoWorkflowPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  const preselectedPrisonerId = searchParams.get('prisonerId') || '';
  
  const [formData, setFormData] = useState<FormData>({
    prisonerId: preselectedPrisonerId,
    workflowType: '',
    incidentType: '',
    incidentDate: new Date().toISOString().split('T')[0],
    incidentTime: new Date().toTimeString().slice(0, 5),
    location: '',
    locationDetail: '',
    description: '',
    immediateMeasures: '',
    witnesses: '',
    priority: 'NORMAL',
    dueDate: '',
    transferType: '',
    targetFacility: '',
    transferReason: '',
  });

  // Gefangene laden für Auswahl
  const { data: prisonersData, isLoading: isPrisonersLoading } = useQuery({
    queryKey: ['prisoners-for-select'],
    queryFn: async () => {
      const response = await api.get<PaginatedResponse<Prisoner>>('/prisoners?pageSize=1000');
      return response;
    },
  });

  const prisoners = prisonersData?.data || [];

  // Workflow erstellen
  const createMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const response = await api.post<CreateWorkflowResponse>('/vgo-workflows', {
        prisonerId: data.prisonerId,
        workflowType: data.workflowType,
        incidentType: data.incidentType,
        incidentDate: data.incidentDate,
        incidentTime: data.incidentTime || undefined,
        location: data.location || undefined,
        locationDetail: data.locationDetail || undefined,
        description: data.description,
        immediateMeasures: data.immediateMeasures || undefined,
        witnesses: data.witnesses || undefined,
        priority: data.priority,
        dueDate: data.dueDate || undefined,
        transferType: data.transferType || undefined,
        targetFacility: data.targetFacility || undefined,
        transferReason: data.transferReason || undefined,
      });
      return response;
    },
    onSuccess: (data) => {
      toast({
        title: 'Vorgang erstellt',
        description: `Vorgang ${data.workflowNumber} wurde erfolgreich erstellt.`,
      });
      navigate(`/vgo-workflows/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Fehler',
        description: error.message || 'Vorgang konnte nicht erstellt werden.',
        variant: 'destructive',
      });
    },
  });

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Bei Wechsel des Workflow-Typs: Incident-Type zurücksetzen
    if (field === 'workflowType') {
      setFormData(prev => ({ ...prev, incidentType: '' }));
      
      // Eilmeldung automatisch auf EILSACHE setzen
      if (value === 'EILMELDUNG') {
        setFormData(prev => ({ ...prev, priority: 'EILSACHE' }));
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung
    if (!formData.prisonerId) {
      toast({ title: 'Fehler', description: 'Bitte wählen Sie einen Gefangenen aus.', variant: 'destructive' });
      return;
    }
    if (!formData.workflowType) {
      toast({ title: 'Fehler', description: 'Bitte wählen Sie eine Vorgangsart.', variant: 'destructive' });
      return;
    }
    if (!formData.incidentType) {
      toast({ title: 'Fehler', description: 'Bitte wählen Sie eine Art des Vorkommnisses.', variant: 'destructive' });
      return;
    }
    if (!formData.description || formData.description.length < 10) {
      toast({ title: 'Fehler', description: 'Bitte geben Sie eine Beschreibung mit mindestens 10 Zeichen ein.', variant: 'destructive' });
      return;
    }

    createMutation.mutate(formData);
  };

  const incidentTypes = INCIDENT_TYPES[formData.workflowType] || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Neuer VGO-Vorgang</h1>
          <p className="text-muted-foreground">
            Erstellen Sie einen neuen Vorgang gemäß Vollzugsgeschäftsordnung
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Grunddaten */}
          <Card>
            <CardHeader>
              <CardTitle>Grunddaten</CardTitle>
              <CardDescription>Wählen Sie Gefangenen und Vorgangsart</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="prisoner">Gefangener *</Label>
                <PrisonerSearchSelect
                  value={formData.prisonerId}
                  onChange={(v) => handleChange('prisonerId', v)}
                  prisoners={prisoners}
                  isLoading={isPrisonersLoading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="workflowType">Vorgangsart *</Label>
                <Select 
                  value={formData.workflowType} 
                  onValueChange={(v) => handleChange('workflowType', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Vorgangsart auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {WORKFLOW_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="incidentType">Art des Vorkommnisses *</Label>
                <Select 
                  value={formData.incidentType} 
                  onValueChange={(v) => handleChange('incidentType', v)}
                  disabled={!formData.workflowType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Art auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {incidentTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priorität</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(v) => handleChange('priority', v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Zeit und Ort */}
          <Card>
            <CardHeader>
              <CardTitle>Zeit und Ort</CardTitle>
              <CardDescription>Wann und wo ist der Vorfall passiert?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="incidentDate">Datum *</Label>
                  <Input
                    id="incidentDate"
                    type="date"
                    value={formData.incidentDate}
                    onChange={(e) => handleChange('incidentDate', e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="incidentTime">Uhrzeit</Label>
                  <Input
                    id="incidentTime"
                    type="time"
                    value={formData.incidentTime}
                    onChange={(e) => handleChange('incidentTime', e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Ort</Label>
                <Select 
                  value={formData.location} 
                  onValueChange={(v) => handleChange('location', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Ort auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map((loc) => (
                      <SelectItem key={loc.value} value={loc.value}>
                        {loc.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="locationDetail">Ortsdetails</Label>
                <Input
                  id="locationDetail"
                  placeholder="z.B. Haftraum 204, Werkstatt Metall"
                  value={formData.locationDetail}
                  onChange={(e) => handleChange('locationDetail', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="dueDate">Frist</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleChange('dueDate', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Sachverhalt */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Sachverhalt</CardTitle>
              <CardDescription>
                Beschreiben Sie den Vorfall sachlich und ohne Wertung (VGO-konform)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="description">Sachverhaltsbeschreibung *</Label>
                <Textarea
                  id="description"
                  placeholder="Beschreiben Sie den Vorfall sachlich und chronologisch..."
                  className="min-h-[150px]"
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Mindestens 10 Zeichen. Sachlich, chronologisch, ohne Wertung.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="immediateMeasures">Erste Maßnahmen</Label>
                <Textarea
                  id="immediateMeasures"
                  placeholder="Welche Maßnahmen wurden bereits ergriffen?"
                  className="min-h-[80px]"
                  value={formData.immediateMeasures}
                  onChange={(e) => handleChange('immediateMeasures', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="witnesses">Zeugen</Label>
                <Input
                  id="witnesses"
                  placeholder="Namen von Zeugen (Bedienstete, Gefangene)"
                  value={formData.witnesses}
                  onChange={(e) => handleChange('witnesses', e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Verlegungsanregung (nur wenn Typ VERLEGUNG_ANREGUNG) */}
          {formData.workflowType === 'VERLEGUNG_ANREGUNG' && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Verlegungsanregung</CardTitle>
                <CardDescription>
                  Zusätzliche Angaben zur Verlegungsanregung
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="transferType">Art der Verlegung</Label>
                    <Select 
                      value={formData.transferType} 
                      onValueChange={(v) => handleChange('transferType', v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Art auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="intern">Intern (innerhalb der JVA)</SelectItem>
                        <SelectItem value="extern">Extern (andere JVA)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="targetFacility">Zielanstalt</Label>
                    <Input
                      id="targetFacility"
                      placeholder="z.B. JVA Köln, Haus B"
                      value={formData.targetFacility}
                      onChange={(e) => handleChange('targetFacility', e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="transferReason">Begründung der Verlegungsanregung</Label>
                  <Textarea
                    id="transferReason"
                    placeholder="Warum wird eine Verlegung angeregt?"
                    className="min-h-[100px]"
                    value={formData.transferReason}
                    onChange={(e) => handleChange('transferReason', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Aktionen */}
        <div className="flex justify-end gap-4 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Abbrechen
          </Button>
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              'Wird erstellt...'
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Vorgang erstellen
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
